/**
 * Adapt popular provider methods to they can work across different popular web3 libs (such as web3x and ethers).
 * `sendAsync`, another popular method, is not being adapted here as we didn't find it necessary in our testing.
 * In case you need to adapt it, please create and issue or send a PR
 */
export class ProviderAdapter {
    /**
     * @param providerType Should be an indicator of the connector that provided the provider.
     * It is only useful to distinguish Wallet Connect V2 requests as their responses have to be handled differently.
     */
    constructor(provider) {
        this.provider = provider;
        this.id = 0;
        this.on = (event, listener) => {
            return this.provider.on(event, listener);
        };
        this.emit = (event, ...args) => {
            return this.provider.emit(event, args);
        };
        this.removeListener = (event, listener) => {
            return this.provider.removeListener(event, listener);
        };
        this.request = async ({ method, params }) => {
            if (this.isModernProvider()) {
                return this.provider.request({
                    method,
                    params
                });
            }
            return this.send(method, params);
        };
        this.sendAsync = async (args, callback) => {
            if (this.hasSendAsync()) {
                return this.provider.sendAsync(args, (err, value) => {
                    // Some providers like Wallet Connect V2 do not wrap the sendAsync response in the standardized JSON-RPC format.
                    // It needs to be wrapped for compatibility with other connectors.
                    if (value && !value.hasOwnProperty('result')) {
                        callback(err, { id: 0, jsonrpc: '2.0', result: value });
                    }
                    else {
                        callback(err, value);
                    }
                });
            }
            return this.send(args, callback);
        };
    }
    static adapt(provider) {
        const providerAdapter = new ProviderAdapter(provider);
        return {
            ...provider,
            on: providerAdapter.on,
            emit: providerAdapter.emit,
            removeListener: providerAdapter.removeListener,
            request: providerAdapter.request,
            sendAsync: providerAdapter.sendAsync,
            send: providerAdapter.send.bind(providerAdapter)
        };
    }
    async send(methodOrArgs, paramsOrCallback) {
        let method;
        let params;
        let callback;
        const hasCallback = typeof paramsOrCallback === 'function';
        if (hasCallback) {
            const args = methodOrArgs; // if sendParams is a function, the first argument has all the other data
            params = args.params || [];
            method = args.method;
            callback = paramsOrCallback;
        }
        else {
            method = methodOrArgs;
            params = paramsOrCallback || [];
            callback = (err, value) => {
                if (err) {
                    throw err;
                }
                return value;
            };
        }
        if (this.isModernProvider()) {
            const [err, result] = await this.provider
                .request({
                method,
                params
            })
                .then(result => [null, result])
                .catch(error => [error, undefined]);
            const returnValue = hasCallback
                ? { id: '', jsonrpc: '2.0', result }
                : result;
            return callback(err, returnValue);
        }
        else {
            this.patchOldMobile();
            const [err, value] = hasCallback
                ? await new Promise(resolve => this.provider.send(methodOrArgs, (err, value) => {
                    resolve([err, value]);
                }))
                : await new Promise(resolve => this.provider.send({
                    jsonrpc: '2.0',
                    id: ++this.id,
                    method,
                    params
                }, (err, value) => {
                    resolve([
                        value && value.hasOwnProperty('error') ? value.error : err,
                        value && value.hasOwnProperty('result') ? value.result : value
                    ]);
                }));
            return callback(err, value);
        }
    }
    isModernProvider() {
        return typeof this.provider['request'] === 'function';
    }
    hasSendAsync() {
        return typeof this.provider['sendAsync'] === 'function';
    }
    patchOldMobile() {
        // Patch for old providers and mobile providers which do not use promises at send as sendAsync
        if (this.provider &&
            !this.provider.isDapper &&
            !this.provider.isFortmatic &&
            !this.provider.isMetamask &&
            this.hasSendAsync() &&
            this.provider.send !== this.provider.sendAsync) {
            // send has to be replaced by sendAsync for old providers
            this.provider.send = this.provider.sendAsync;
        }
    }
}
//# sourceMappingURL=ProviderAdapter.js.map